import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell, TableRow, TableSortLabel,
    IconButton, Tooltip, Switch,
    Popover, Typography, Divider,
    Snackbar, Alert, Box, LinearProgress,
} from '@mui/material';
import { Add, Edit, Delete, Business, Visibility } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Loader } from '../../../shared/components/Loader';
import { clientService } from '../../../shared/services/clientService';
import { Client } from '../../../shared/types/client';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { ClientFilters, ClientFilterValues } from './ClientFilters';

export const Clients: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();

    const [clients, setClients] = useState<Client[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [appliedFilters, setAppliedFilters] = useState<ClientFilterValues>({
        country: '',
        state: '',
        city: '',
        status: 'all',
    });
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

    const fetchClients = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await clientService.list({
                page: currentPage + 1,
                limit: pageSize,
            });

            const outerData = res?.data;
            const items = outerData?.data || outerData?.items || (outerData as any)?.clients || [];

            setClients(
                (Array.isArray(items) ? items : []).map((c: any) => ({
                    ...c,
                    _id: c._id ?? c.id,
                    isActive: c.isActive ?? c.is_active ?? true,
                }))
            );
        } catch (err: any) {
            setSnackbar({ open: true, message: 'Error loading clients', severity: 'error' });
            setClients([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // Client-side filtering & sorting
    const filteredClients = React.useMemo(() => {
        let list = [...clients];

        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(c =>
                c.companyName?.toLowerCase().includes(q) ||
                c.ownerName?.toLowerCase().includes(q) ||
                c.email?.toLowerCase().includes(q) ||
                c.city?.toLowerCase().includes(q) ||
                c.phone?.toLowerCase().includes(q)
            );
        }

        if (appliedFilters.country) {
            list = list.filter((c) => c.country?.toLowerCase() === appliedFilters.country.toLowerCase());
        }
        if (appliedFilters.state) {
            list = list.filter((c) => c.state?.toLowerCase() === appliedFilters.state.toLowerCase());
        }
        if (appliedFilters.city) {
            list = list.filter((c) => c.city?.toLowerCase() === appliedFilters.city.toLowerCase());
        }
        if (appliedFilters.status !== 'all') {
            const shouldBeActive = appliedFilters.status === 'active';
            list = list.filter((c) => Boolean(c.isActive) === shouldBeActive);
        }


        if (sortData?.active) {
            list.sort((a, b) => {
                const av = (a as any)[sortData.active] ?? '';
                const bv = (b as any)[sortData.active] ?? '';
                if (typeof av === 'string' && typeof bv === 'string')
                    return sortData.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                if (av < bv) return sortData.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortData.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return list;
    }, [clients, searchInput, appliedFilters, sortData]);

    const paginatedClients = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredClients.slice(start, start + pageSize);
    }, [filteredClients, currentPage, pageSize]);

    const handleSort = (col: string) => {
        setSortData(prev =>
            prev?.active === col
                ? { active: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { active: col, direction: 'asc' }
        );
    };

    const handleToggleStatus = async (client: Client) => {
        try {
            await clientService.toggleStatus(client._id);
            setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
            fetchClients();
        } catch {
            setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
        }
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(e.currentTarget);
        setSelectedClientId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedClientId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedClientId) return;
        try {
            await clientService.delete(selectedClientId);
            setSnackbar({ open: true, message: 'Client deleted successfully', severity: 'success' });
            fetchClients();
        } catch (error: any) {
            const msg = error?.response?.data?.message || 'Error deleting client';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            handleDeleteClose();
        }
    };

    const SortableHeader = ({ col, label }: { col: string; label: string }) => (
        <TableCell className="!font-semibold !text-gray-700">
            <TableSortLabel
                active={sortData?.active === col}
                direction={sortData?.active === col ? sortData.direction : 'asc'}
                onClick={() => handleSort(col)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Business className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Clients
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={(v) => { setSearchInput(v); setCurrentPage(0); }}
                        onSearch={() => setCurrentPage(0)}
                        placeholder="Search"
                        className="w-full sm:w-64"
                    />
                    {(isSuperAdmin() || hasPermission('clients.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<Add />}
                            onClick={() => navigate('/clients/add')}
                            sx={{ px: 3, py: 1.5, borderRadius: '6px' }}
                        >
                            Add Client
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <ClientFilters
                    onApply={(filters) => {
                        setAppliedFilters(filters);
                        setCurrentPage(0);
                    }}
                    onReset={() => {
                        setAppliedFilters({
                            country: '',
                            state: '',
                            city: '',
                            status: 'all',
                        });
                        setCurrentPage(0);
                    }}
                />

                <div className="flex flex-col w-full flex-1">
                    {isLoading && clients.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && clients.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <SortableHeader col="companyName" label="Company" />
                                    <SortableHeader col="ownerName" label="Owner" />
                                    <SortableHeader col="email" label="Email" />
                                    <SortableHeader col="phone" label="Phone" />
                                    <SortableHeader col="city" label="City" />
                                    <SortableHeader col="isActive" label="Status" />
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredClients.length === 0}
                            emptyColSpan={8}
                        >
                            {paginatedClients.map((item, i) => (
                                <TableRow
                                    key={item._id}
                                    className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                    <TableCell className="!text-gray-500 !text-sm">
                                        {currentPage * pageSize + i + 1}
                                    </TableCell>
                                    <TableCell className="!text-gray-900 !font-medium">
                                        {item.displayName || item.companyName || item.ownerName || '—'}
                                    </TableCell>
                                    <TableCell className="!text-gray-700">{item.ownerName || '—'}</TableCell>
                                    <TableCell className="!text-gray-600">{item.email || '—'}</TableCell>
                                    <TableCell className="!text-gray-600">{item.phone || '—'}</TableCell>
                                    <TableCell className="!text-gray-600">
                                        {[item.city, item.state].filter(Boolean).join(', ')}
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={item.isActive}
                                            onChange={() => handleToggleStatus(item)}
                                            disabled={isLoading}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Tooltip title="View">
                                                <IconButton
                                                    onClick={() => navigate(`/clients/view/${item._id}`)}
                                                    size="small"
                                                    className="text-gray-600 hover:bg-gray-100"
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {(isSuperAdmin() || hasPermission('clients.update')) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        onClick={() => navigate(`/clients/edit/${item._id}`)}
                                                        size="small"
                                                        className="text-blue-600 hover:bg-blue-50"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() || hasPermission('clients.delete')) && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={(e) => handleDeleteClick(e, item._id)}
                                                        size="small"
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredClients.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            {/* Delete Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        p: 0, mt: 1.5, width: 260,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Delete Client?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This client will be soft-deleted and hidden from the list.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton variant="secondary" size="small" onClick={handleDeleteClose}>Cancel</AppButton>
                        <AppButton variant="danger" size="small" onClick={handleDeleteConfirm}>Delete</AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
